import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import DropDownStyles, { DropDownStylesDark } from "../../../pages/staff/dropdownStyles";

const CountryCitySelector = ({
  selectedCountry,
  selectedCity,
  setSelectedCity,
  onChange,
  setLoading,
  selectedValueIsLabel = false,
  countryFieldName = "CompCountry",
  cityFieldName = "CompCity",
  isDarkMode = false,
  /** Must be above CustomModal (often 10050+) so menus are clickable inside modals */
  menuZIndex = 11000,
}) => {
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);

  const baseDropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const dropdownStyles = useMemo(
    () => ({
      ...baseDropdownStyles,
      menu: (provided) => ({
        ...baseDropdownStyles.menu(provided),
        zIndex: menuZIndex,
      }),
      menuPortal: (provided) => ({
        ...provided,
        zIndex: menuZIndex,
      }),
    }),
    [baseDropdownStyles, menuZIndex]
  );

  const resolveCountryCode = useCallback(
    (countryValue) => {
      if (!countryValue) return "";
      const match = countries.find(
        (c) =>
          c.value === countryValue ||
          c.label === countryValue ||
          String(c.label).toLowerCase() === String(countryValue).toLowerCase()
      );
      return match?.value || countryValue;
    },
    [countries]
  );

  const resolveCountryOption = useCallback(
    (countryValue) => {
      if (!countryValue) return null;
      return (
        countries.find(
          (c) =>
            c.value === countryValue ||
            c.label === countryValue ||
            String(c.label).toLowerCase() === String(countryValue).toLowerCase()
        ) || null
      );
    },
    [countries]
  );

  const resolveCityOption = useCallback(
    (cityValue) => {
      if (!cityValue) return null;
      const match = cities.find((c) =>
        selectedValueIsLabel ? c.label === cityValue : String(c.value) === String(cityValue)
      );
      if (match) return match;
      return { value: cityValue, label: cityValue };
    },
    [cities, selectedValueIsLabel]
  );

  const fetchCountries = useCallback(async () => {
    try {
      setLoading && setLoading(true);
      const response = await axiosInstance.get(APIs.GET_COUNTRIES);
      const countryData = response.data.data || response.data;

      setCountries(
        countryData.map((country) => ({
          value: country.code,
          label: country.name,
        }))
      );
    } catch (err) {
      // Error fetching countries silently
    } finally {
      setLoading && setLoading(false);
    }
  }, []); // Remove setLoading from dependencies to prevent infinite loop

  const fetchCities = useCallback(
    async (countryCode) => {
      if (!countryCode) {
        setCities([]);
        return;
      }

      try {
        setLoading && setLoading(true);
        const response = await axiosInstance.get(APIs.GET_ALL_CITIES, {
          params: { CountryCode: countryCode },
        });
        const cityData = response.data.data || response.data;

        if (cityData) {
          const mappedCities = cityData.map((city) => ({
            value: city.cit_ID,
            label: city.cit_Name,
          }));
          setCities(mappedCities);
        } else {
          setCities([]);
        }
      } catch (err) {
        setCities([]);
      } finally {
        setLoading && setLoading(false);
      }
    },
    []
  );

  const handleCountryChange = (option) => {
    const countryCode = option?.value || "";
    const countryChanged = countryCode !== resolveCountryCode(selectedCountry);

    if (onChange) {
      const payload = { [countryFieldName]: countryCode };
      if (countryChanged) {
        payload[cityFieldName] = "";
      }
      onChange(payload);
    }

    if (countryChanged && setSelectedCity) {
      setSelectedCity("");
    }

    if (countryCode) {
      fetchCities(countryCode);
    } else {
      setCities([]);
    }
  };

  const handleCityChange = (option, actionMeta) => {
    let cityValue = "";
    if (option) {
      if (actionMeta.action === "create-option") {
        cityValue = option.label;
      } else {
        cityValue = selectedValueIsLabel ? option.label : option.value;
      }
    }

    if (setSelectedCity) {
      setSelectedCity(cityValue);
    }

    if (onChange) {
      onChange({
        [countryFieldName]: selectedCountry,
        [cityFieldName]: cityValue,
      });
    }
  };

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  useEffect(() => {
    const countryCode = resolveCountryCode(selectedCountry);
    if (countryCode) {
      fetchCities(countryCode);
    } else {
      setCities([]);
    }
  }, [selectedCountry, resolveCountryCode, fetchCities]);

  return (
    <>
      <div className="col-6">
        <label className={`form-label ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>Country</label>
        <div className="input-group">
          <span className={`input-group-text ${isDarkMode ? 'bg-dark text-white' : 'bg-light text-muted'}`}>
            <i className="fa fa-globe"></i>
          </span>
          <Select
            value={resolveCountryOption(selectedCountry)}
            onChange={handleCountryChange}
            options={countries}
            styles={dropdownStyles}
            placeholder="Select Country"
            isClearable={true}
            className="flex-grow-1"
            menuPortalTarget={document.body}
            menuPosition="fixed"
            menuShouldBlockScroll={false}
            menuShouldScrollIntoView={false}
            closeMenuOnScroll={false}
          />
        </div>
      </div>

      <div className="col-6">
        <label className={`form-label ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>City</label>
        <CreatableSelect
          value={resolveCityOption(selectedCity)}
          onChange={handleCityChange}
          options={cities}
          styles={dropdownStyles}
          placeholder="Select or type City"
          isClearable={true}
          isDisabled={!selectedCountry}
          formatCreateLabel={(inputValue) => `Use "${inputValue}"`}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          menuShouldBlockScroll={false}
          menuShouldScrollIntoView={false}
          closeMenuOnScroll={false}
        />
      </div>
    </>
  );
};

export default CountryCitySelector;
